import { Component } from 'react'
import { toast } from 'react-toastify'
import Loader from '../../../components/loader'
import { getLocale, getString } from '../../../locale'
import { getPumpUsageList, replacePumpUsage, PumpUsageEntry } from '../../../api/0x0005/drinks'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    replacingChannel?: number
    entries: PumpUsageEntry[]
}

function formatRuntime(runtimeMs: number): string {
    const totalSeconds = Math.max(0, Math.floor(runtimeMs / 1000))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

export default class PumpUsageTab extends Component<Props, State> {
    state: State = {
        loading: true,
        replacingChannel: undefined,
        entries: [],
    }

    componentDidMount() {
        this.load()
    }

    load = async () => {
        this.setState({ loading: true })
        try {
            const response = await getPumpUsageList(this.props.clientId)
            this.setState({ entries: response.data.entries || [], loading: false })
        } catch (error: any) {
            toast.error(error.message || getString('pump_usage.load_error'))
            this.setState({ loading: false })
        }
    }

    replace = async (entry: PumpUsageEntry) => {
        this.setState({ replacingChannel: entry.channelNum })
        try {
            const response = await replacePumpUsage(this.props.clientId, entry.channelNum)
            if (response.data.success === 0) {
                throw new Error(response.data.errorMsg || getString('pump_usage.replace_error'))
            }
            toast.success(getString('pump_usage.replace_success'))
            await this.load()
        } catch (error: any) {
            toast.error(error.message || getString('pump_usage.replace_error'))
            this.setState({ replacingChannel: undefined })
        }
    }

    render() {
        const { loading, entries, replacingChannel } = this.state
        const boolLabel = (value: number) => (getLocale() === 'he' ? (value === 1 ? 'כן' : 'לא') : value === 1 ? 'Yes' : 'No')
        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <Loader />
                </div>
            )
        }

        return (
            <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{getString('pump_usage.title')}</h1>
                    <button
                        onClick={this.load}
                        className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {getString('settings.refresh')}
                    </button>
                </div>

                {entries.length === 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 text-gray-600 dark:text-gray-300">
                        {getString('pump_usage.empty')}
                    </div>
                )}

                {entries.map(entry => (
                    <div key={entry.channelNum} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                {getString('channels.channel')} {entry.channelNum}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                {getString('pump_usage.mode')}: {entry.mode === 0 ? getString('channels.mode_solenoid') : getString('channels.mode_relay')}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-3 text-sm text-gray-700 dark:text-gray-300">
                            <div>{getString('pump_usage.active')}: {boolLabel(entry.active)}</div>
                            <div>{getString('pump_usage.is_on')}: {boolLabel(entry.isOn)}</div>
                            <div>{getString('pump_usage.runtime')}: {formatRuntime(entry.runtimeMs)}</div>
                        </div>

                        <div className="text-right">
                            <button
                                onClick={() => this.replace(entry)}
                                disabled={replacingChannel === entry.channelNum}
                                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white"
                            >
                                {replacingChannel === entry.channelNum ? getString('common.loading') : getString('pump_usage.replace')}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        )
    }
}
